<template>
    <!-- Desktop Table (visible only on 2xl screens and above) -->
    <div class="hidden 2xl:block overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead class="bg-gray-50 dark:bg-black">
          <tr>
            <th scope="col" class="px-3 sm:px-6 py-4 text-center text-xs font-bold text-slate-800 dark:text-gray-400 uppercase tracking-wider">
              Claim Number
            </th>
            <th scope="col" class="px-3 sm:px-6 py-4 text-center text-xs font-bold text-slate-800 dark:text-gray-400 uppercase tracking-wider">
              Customer
            </th>
            <th scope="col" class="px-3 sm:px-6 py-4 text-center text-xs font-bold text-slate-800 dark:text-gray-400 uppercase tracking-wider">
              Brand
            </th>
            <!-- <th scope="col" class="px-3 sm:px-6 py-4 text-center text-xs font-bold text-slate-800 dark:text-gray-400 uppercase tracking-wider">
              Vehicle/Plate No.
            </th> -->
            <th scope="col" class="px-3 sm:px-6 py-4 text-center text-xs font-bold text-slate-800 dark:text-gray-400 uppercase tracking-wider">
              Tire Pattern
            </th>
            <th scope="col" class="px-3 sm:px-6 py-4 text-center text-xs font-bold text-slate-800 dark:text-gray-400 uppercase tracking-wider">
              Date Submitted
            </th>
            <th scope="col" class="px-3 sm:px-6 py-4 text-center text-xs font-bold text-slate-800 dark:text-gray-400 uppercase tracking-wider">
              Status
            </th>
            <!-- <th scope="col" class="px-3 sm:px-6 py-4 text-center text-xs font-bold text-slate-800 dark:text-gray-400 uppercase tracking-wider">
              Approval
            </th> -->
            <th scope="col" class="px-3 sm:px-6 py-4 text-center text-xs font-bold text-slate-800 dark:text-gray-400 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody class="bg-white dark:bg-black divide-y divide-gray-200 dark:divide-gray-700">
          <tr
            v-for="claim in claims"
            :key="claim.claim_id"
            class="transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-600 odd:bg-white even:bg-gray-25 dark:odd:bg-gray-900 dark:even:bg-gray-800"
          >
            <!-- Claim Number -->
            <td class="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white text-center">
              <div class="text-xs sm:text-sm font-medium">
                {{ claim.claim_number }}
              </div>
            </td>

            <!-- Customer Name -->
            <td class="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-center">
              <div class="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                {{ claim.user?.first_name }} {{ claim.user?.last_name }}
              </div>
            </td>

            <!-- Brand -->
            <td class="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-center">
              <span
                class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
                :class="getBrandClass(claim.brand?.brand_name)"
              >
                {{ claim.brand?.brand_name || 'N/A' }}
              </span>
            </td>

            <!-- Vehicle/Plate No. -->
            <!-- <td class="px-3 sm:px-6 py-4 text-sm text-center text-gray-900 dark:text-white">
              <div class="font-medium text-xs sm:text-sm">{{ claim.vehicle?.vehicle_make }} {{ claim.vehicle?.vehicle_model }}</div>
              <div class="text-xs text-gray-500 dark:text-gray-400">
                {{ claim.vehicle?.plate_number }}
              </div>
            </td> -->

            <!-- Tire Pattern -->
            <td class="px-3 sm:px-6 py-4 text-sm text-center text-gray-900 dark:text-white">
              <div class="font-medium text-xs sm:text-sm">{{ claim.tire?.pattern }}</div>
            </td>

            <!-- Date Submitted -->
            <td class="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-center">
              <div class="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                {{ formatDate(claim.created_at) }}
              </div>
            </td>

            <!-- Status -->
            <td class="px-3 sm:px-6 py-4 whitespace-nowrap text-center">
              <span
                class="inline-flex items-center px-2 sm:px-2.5 py-1 rounded-full text-xs font-medium"
                :class="getStatusClass(claim.claim_status)"
              >
                <span
                  class="w-1.5 h-1.5 rounded-full mr-1 sm:mr-1.5"
                  :class="getStatusDotClass(claim.claim_status)"
                ></span>
                {{ claim.claim_status }}
              </span>
            </td>

            <!-- Actions -->
            <td class="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 text-center">
              <div class="flex items-center justify-center space-x-1 lg:space-x-2">
                <div class="relative group inline-block">
                  <button
                    @click.stop="$emit('view', claim)"
                    :disabled="loading"
                    class="inline-flex items-center p-1.5 border border-transparent rounded-md text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ViewIcon classes="h-5 w-5" />
                  </button>
                  <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 w-max text-xs text-white bg-gray-800 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none z-10">
                    View Details
                  </div>
                </div>
                <div class="relative group inline-block">
                  <button
                    @click.stop="$emit('edit', claim)"
                    :disabled="loading"
                    class="inline-flex items-center p-1.5 border border-transparent rounded-md text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <EditIcon classes="h-5 w-5" />
                  </button>
                  <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 w-max text-xs text-white bg-gray-800 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none z-10">
                    Edit Claim
                  </div>
                </div>
                <div class="relative group inline-block">
                  <button
                    @click.stop="$emit('delete', claim)"
                    :disabled="loading"
                    class="inline-flex items-center p-1.5 border border-transparent rounded-md text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <DeleteIcon classes="h-5 w-5" />
                  </button>
                  <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 w-max text-xs text-white bg-gray-800 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none z-10">
                    Delete Claim
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Mobile Cards (visible on 2xl screens and below) -->
    <div class="2xl:hidden divide-y divide-gray-200 dark:divide-gray-700">
      <div
        v-for="claim in claims"
        :key="claim.claim_id"
        class="p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150"
      >
        <!-- Header: Claim Number and Status -->
        <div class="flex items-center justify-between mb-3">
          <div class="flex items-center space-x-2">
            <span class="text-sm font-medium text-gray-900 dark:text-white">
              {{ claim.claim_number }}
            </span>
          </div>
          <span
            class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
            :class="getStatusClass(claim.claim_status)"
          >
            <span
              class="w-1.5 h-1.5 rounded-full mr-1.5"
              :class="getStatusDotClass(claim.claim_status)"
            ></span>
            {{ claim.claim_status }}
          </span>
        </div>

        <!-- Customer Info -->
        <div class="grid grid-cols-2 gap-3 mb-3">
          <div>
            <p class="text-xs text-gray-500 dark:text-gray-400">Customer</p>
            <p class="text-sm text-gray-900 dark:text-white">
              {{ claim.user?.first_name }} {{ claim.user?.last_name }}
            </p>
          </div>
          <div>
            <p class="text-xs text-gray-500 dark:text-gray-400">Brand</p>
            <span
              class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
              :class="getBrandClass(claim.brand?.brand_name)"
            >
              {{ claim.brand?.brand_name || 'N/A' }}
            </span>
          </div>
        </div>

        <!-- Vehicle/Plate No. -->
        <!-- <div class="mb-3">
          <div class="text-xs text-gray-500 dark:text-gray-400 mb-1">Vehicle/Plate No.</div>
          <div class="font-medium text-gray-900 dark:text-white">
            {{ claim.vehicle?.vehicle_make }} {{ claim.vehicle?.vehicle_model }}
          </div>
          <div class="text-sm text-gray-500 dark:text-gray-400">
            {{ claim.vehicle?.plate_number }}
          </div>
        </div> -->

        <!-- Tire Pattern -->
        <div class="mb-3">
          <div class="text-xs text-gray-500 dark:text-gray-400 mb-1">Tire Pattern</div>
          <div class="font-medium text-gray-900 dark:text-white text-sm">{{ claim.tire?.pattern }}</div>
        </div>

        <!-- Date Submitted -->
        <div class="mb-3">
          <p class="text-xs text-gray-500 dark:text-gray-400">Date Submitted</p>
          <p class="text-sm text-gray-900 dark:text-white">
            {{ formatDate(claim.created_at) }}
          </p>
        </div>

        <!-- Approval Buttons (for under review claims) -->
        <div v-if="claim.claim_status === 'under review'" class="flex items-center space-x-2 mb-3">
          <button
            @click.stop="$emit('approve', claim)"
            :disabled="loading"
            class="flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            Approve
          </button>
          <button
            @click.stop="$emit('reject', claim)"
            :disabled="loading"
            class="flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
            Reject
          </button>
        </div>

        <!-- Action Buttons -->
        <div class="flex items-center space-x-2">
          <button
            @click.stop="$emit('view', claim)"
            :disabled="loading"
            class="flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ViewIcon classes="h-4 w-4 mr-1.5" />
            View
          </button>
          <button
            @click.stop="$emit('edit', claim)"
            :disabled="loading"
            class="flex-1 inline-flex items-center justify-center px-3 py-2 border border-amber-300 dark:border-amber-600 text-sm font-medium rounded-md text-amber-700 dark:text-amber-400 bg-white dark:bg-black hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <EditIcon classes="h-4 w-4 mr-1.5" />
            Edit
          </button>
          <button
            @click.stop="$emit('delete', claim)"
            :disabled="loading"
            class="flex-1 inline-flex items-center justify-center px-3 py-2 border border-red-300 dark:border-red-600 text-sm font-medium rounded-md text-red-700 dark:text-red-400 bg-white dark:bg-black hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <DeleteIcon classes="h-4 w-4 mr-1.5" />
            Delete
          </button>
        </div>
      </div>
    </div>
</template>

<script setup lang="ts">
import type { Claim } from '@/types/claim';
import ViewIcon from '@/components/icons/ViewIcon.vue';
import EditIcon from '@/components/icons/EditIcon.vue';
import DeleteIcon from '@/components/icons/DeleteIcon.vue';

defineProps<{
  claims: Claim[];
  loading?: boolean;
}>();

defineEmits<{
  approve: [claim: Claim];
  reject: [claim: Claim];
  view: [claim: Claim];
  edit: [claim: Claim];
  delete: [claim: Claim];
}>();

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

function getStatusClass(status: string): string {
  switch (status) {
    case 'under review':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
    case 'approved':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    case 'rejected':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
    case 'ready for pick-up':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
    case 'fulfilled':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
  }
}

function getStatusDotClass(status: string): string {
  switch (status) {
    case 'under review':
      return 'bg-yellow-500 dark:bg-yellow-400';
    case 'approved':
      return 'bg-green-500 dark:bg-green-400';
    case 'rejected':
      return 'bg-red-500 dark:bg-red-400';
    case 'ready for pick-up':
      return 'bg-blue-500 dark:bg-blue-400';
    case 'fulfilled':
      return 'bg-purple-500 dark:bg-purple-400';
    default:
      return 'bg-gray-500 dark:bg-gray-400';
  }
}

function getBrandClass(brandName?: string): string {
  switch (brandName?.toLowerCase()) {
    case 'continental':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
    case 'falken':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
  }
}
</script>

